import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function ExpenseTracker() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ amount: '', category: '', date: new Date().toISOString().split('T')[0], description: '' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PER_PAGE = 20;

  const categories = ['Housing', 'Food', 'Transport', 'Utilities', 'Entertainment', 'Healthcare', 'Education', 'Shopping', 'Travel', 'Other'];

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/expenses'); const responseData = res.data; const data = Array.isArray(responseData?.data) ? responseData.data : Array.isArray(responseData) ? responseData : (responseData?.data || []);
      setExpenses(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.category || !formData.date) { toast.error('Fill all fields'); return; }
    setLoading(true);
    try {
      editingItem ? await api.put('/expenses/' + editingItem._id, formData) : await api.post('/expenses', formData);
      toast.success(editingItem ? 'Updated!' : 'Added!');
      setShowForm(false); setEditingItem(null);
      setFormData({ amount: '', category: '', date: new Date().toISOString().split('T')[0], description: '' });
      fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete('/expenses/' + id); toast.success('Deleted'); fetchExpenses(); }
    catch (e) { toast.error('Failed'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);
  const filtered = expenses.filter(e => e.category?.toLowerCase().includes(search.toLowerCase()) || e.description?.toLowerCase().includes(search.toLowerCase()));
  const total = filtered.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const paginated = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const maxPage = Math.ceil(filtered.length / PER_PAGE);

  return (
    <div className="space-y-3 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Expense Tracker</h1>
          <p className="text-xs sm:text-sm text-gray-500">{expenses.length} records | Total: <span className="text-red-600 font-bold">{formatCurrency(total)}</span></p>
        </div>
        <button onClick={() => { setEditingItem(null); setFormData({ amount: '', category: '', date: new Date().toISOString().split('T')[0], description: '' }); setShowForm(!showForm); }}
          className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 shadow-sm">{showForm ? '✕ Cancel' : '+ Add Expense'}</button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="font-semibold mb-3">{editingItem ? 'Edit' : 'Add'} Expense</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="number" step="0.01" placeholder="Amount *" required value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" />
            <select required value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm">
              <option value="">Category *</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" />
            <input type="text" placeholder="Description" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" />
            <button type="submit" disabled={loading} className="sm:col-span-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">{loading ? 'Saving...' : editingItem ? 'Update' : 'Add'}</button>
          </form>
        </div>
      )}

      {filtered.length > 0 && <input type="text" placeholder="🔍 Search expenses..." value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />}

      {loading && !showForm ? (
        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : paginated.length === 0 ? (
        <div className="text-center py-12 sm:py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <span className="text-4xl sm:text-5xl">💸</span>
          <h3 className="font-semibold mt-3">{search ? 'No matches' : 'No Expenses Yet'}</h3>
          <p className="text-gray-500 text-sm mt-1">{search ? 'Try another search' : 'Add your first expense'}</p>
        </div>
      ) : (
        <>
          <div className="sm:hidden space-y-2">
            {paginated.map(exp => (
              <div key={exp._id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
                <div className="flex justify-between items-start">
                  <div><p className="font-medium text-sm">{exp.category}</p><p className="text-xs text-gray-500">{new Date(exp.date).toLocaleDateString()}</p>{exp.description && <p className="text-xs text-gray-400 mt-0.5">{exp.description}</p>}</div>
                  <p className="text-red-600 font-bold text-sm">{formatCurrency(exp.amount)}</p>
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => { setEditingItem(exp); setFormData({ amount: exp.amount, category: exp.category, date: exp.date?.split('T')[0] || '', description: exp.description || '' }); setShowForm(true); }} className="text-indigo-600 text-xs">Edit</button>
                  <button onClick={() => handleDelete(exp._id)} className="text-red-600 text-xs">Delete</button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full"><thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th></tr></thead>
              <tbody className="divide-y divide-gray-200">
                {paginated.map(exp => (
                  <tr key={exp._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{new Date(exp.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm font-medium">{exp.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[150px]">{exp.description || '-'}</td>
                    <td className="px-4 py-3 text-sm text-red-600 font-semibold text-right">{formatCurrency(exp.amount)}</td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      <button onClick={() => { setEditingItem(exp); setFormData({ amount: exp.amount, category: exp.category, date: exp.date?.split('T')[0] || '', description: exp.description || '' }); setShowForm(true); }} className="text-indigo-600 hover:text-indigo-800">Edit</button>
                      <button onClick={() => handleDelete(exp._id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
          {maxPage > 1 && (
            <div className="flex justify-center gap-2">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1.5 bg-white border rounded-lg text-sm disabled:opacity-50">← Prev</button>
              <span className="px-3 py-1.5 text-sm text-gray-600">Page {page + 1} of {maxPage}</span>
              <button onClick={() => setPage(Math.min(maxPage - 1, page + 1))} disabled={page >= maxPage - 1} className="px-3 py-1.5 bg-white border rounded-lg text-sm disabled:opacity-50">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}